import { fetchWithChatFeedback } from './chatFeedbackRequired';
import type {
  SupportAssignee,
  SupportIssue,
  SupportIssueAdminPatch,
} from '../types/supportIssue';
import {
  mockSupportAssignees,
  mockSupportIssues,
} from '../mockData/supportIssues';
import { extractDetailMessage } from '../utils/userFacingError';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

interface IssueListResponse {
  issues: SupportIssue[];
}

interface IssueResponse {
  issue: SupportIssue;
}

interface AssigneeListResponse {
  assignees: SupportAssignee[];
}

export interface SupportIssueStats {
  received: number;
  resolved: number;
  average_completion_hours: number | null;
  median_completion_hours: number | null;
  sources: Array<{ source: string; count: number }>;
}

interface SupportIssueStatsResponse {
  start: string;
  end: string;
  summary: SupportIssueStats;
}

interface SupportIssueDashboardResponse extends SupportIssueStatsResponse {
  issues: SupportIssue[];
}

export interface SupportIssueDashboard {
  issues: SupportIssue[];
  summary: SupportIssueStats;
}

export interface FeedbackResponseEmailDelivery {
  event_key: string;
  event_type: 'update' | 'resolution' | string;
  status: string;
  scheduled_at?: string | null;
  read_at?: string | null;
  cancelled_at?: string | null;
  attempted_at?: string | null;
  accepted_at?: string | null;
  failed_at?: string | null;
  failure_code?: string | null;
}

export interface FeedbackResponseEmailDeliveryHealth {
  pending_due_count?: number;
  oldest_due_at?: string | null;
  counts?: Record<string, number>;
  last_tick_completed_at?: string;
  last_error_code?: string;
}

export async function fetchFeedbackResponseEmailDeliveries(options: {
  status?: string;
  offset?: number;
  limit?: number;
} = {}): Promise<{
  deliveries: FeedbackResponseEmailDelivery[];
  health: FeedbackResponseEmailDeliveryHealth;
  nextOffset: number | null;
}> {
  const query = new URLSearchParams();
  if (options.status) query.set('status', options.status);
  if (Number.isSafeInteger(options.offset) && options.offset! > 0) query.set('offset', String(options.offset));
  if (Number.isSafeInteger(options.limit) && options.limit! > 0) query.set('limit', String(options.limit));
  const response = await fetchIssueListResponse(
    `/api/admin/support/feedback-response-email-deliveries${query.toString() ? `?${query}` : ''}`,
  );
  handleUnauthorized(response);
  if (!response.ok) throw await responseError(response);
  const payload = await response.json() as {
    deliveries?: FeedbackResponseEmailDelivery[];
    health?: FeedbackResponseEmailDeliveryHealth;
    next_offset?: number | null;
  };
  return {
    deliveries: Array.isArray(payload.deliveries) ? payload.deliveries : [],
    health: payload.health || {},
    nextOffset: Number.isSafeInteger(payload.next_offset) ? payload.next_offset! : null,
  };
}

function cloneSupportIssue(issue: SupportIssue): SupportIssue {
  return {
    ...issue,
    customer_updates: issue.customer_updates.map((update) => ({ ...update })),
    customer_comments: (issue.customer_comments || []).map((comment) => ({
      ...comment,
    })),
    internal_notes: issue.internal_notes?.map((note) => ({ ...note })),
    internal_resolutions: issue.internal_resolutions?.map((resolution) => ({
      ...resolution,
    })),
  };
}

let mockIssueState = mockSupportIssues.map(cloneSupportIssue);

function replaceMockIssue(issue: SupportIssue): SupportIssue {
  mockIssueState = mockIssueState.map((row) =>
    row.id === issue.id ? cloneSupportIssue(issue) : row
  );
  return cloneSupportIssue(issue);
}

function handleUnauthorized(response: Response): void {
  if (response.status === 401) {
    window.dispatchEvent(new Event('auth-expired'));
  }
}

async function fetchIssueListResponse(path: string): Promise<Response> {
  let lastError: unknown;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const response = await fetchWithChatFeedback(`${API_BASE_URL}${path}`, {
        credentials: 'include',
      });
      if (attempt === 0 && response.status >= 500) continue;
      return response;
    } catch (error) {
      lastError = error;
      if (attempt === 1) throw error;
    }
  }
  throw lastError instanceof Error
    ? lastError
    : new Error("We couldn't load your feedback. Please try again.");
}

async function responseError(response: Response): Promise<Error> {
  const body = await response.json().catch(() => ({}));
  const detail = body?.detail ?? body;
  const message = extractDetailMessage(
    detail,
    "We couldn't load your feedback. Please try again."
  );
  const error = new Error(message) as Error & { error_code?: string };
  if (detail && typeof detail === 'object' && detail.error_code) {
    error.error_code = String(detail.error_code);
  }
  return error;
}

export async function fetchSupportIssues(
  adminView = false,
  developerView = false
): Promise<SupportIssue[]> {
  if (process.env.REACT_APP_USE_MOCK === 'true') {
    if (adminView && developerView) {
      return mockIssueState.map(cloneSupportIssue);
    }
    return mockIssueState.map((sourceIssue) => {
      const issue = cloneSupportIssue(sourceIssue);
      const {
        github_issue_number: _githubNumber,
        github_issue_url: _githubUrl,
        github_sync_state: _githubState,
        github_assignee_sync_state: _githubAssigneeState,
        internal_notes: _internalNotes,
        internal_resolutions: _internalResolutions,
        ...visibleIssue
      } = issue;
      if (adminView) return visibleIssue;
      const {
        user_email: _userEmail,
        user_unit: _userUnit,
        priority: _priority,
        backlog: _backlog,
        owner: _owner,
        planned_start: _plannedStart,
        target_completion: _targetCompletion,
        has_unread_customer_comment: _hasUnreadCustomerComment,
        customer_comment_version: _customerCommentVersion,
        ...customerIssue
      } = visibleIssue;
      return customerIssue;
    });
  }
  const path = adminView
    ? '/api/admin/support/issues'
    : '/api/support/issues';
  const response = await fetchIssueListResponse(path);
  handleUnauthorized(response);
  if (!response.ok) throw await responseError(response);
  const payload = (await response.json()) as IssueListResponse;
  return Array.isArray(payload.issues) ? payload.issues : [];
}

export async function fetchSupportIssueStats(
  start: string,
  end: string,
): Promise<SupportIssueStats> {
  const query = new URLSearchParams({ start, end });
  const response = await fetchIssueListResponse(
    `/api/admin/support/issues/stats?${query.toString()}`,
  );
  handleUnauthorized(response);
  if (!response.ok) throw await responseError(response);
  const payload = (await response.json()) as SupportIssueStatsResponse;
  return payload.summary;
}

export async function fetchSupportIssueDashboard(
  start: string,
  end: string,
  developerView = false,
): Promise<SupportIssueDashboard> {
  if (process.env.REACT_APP_USE_MOCK === 'true') {
    return {
      issues: await fetchSupportIssues(true, developerView),
      summary: {
        received: 0,
        resolved: 0,
        average_completion_hours: null,
        median_completion_hours: null,
        sources: [],
      },
    };
  }
  const query = new URLSearchParams({ stats_start: start, stats_end: end });
  const response = await fetchIssueListResponse(
    `/api/admin/support/issues?${query.toString()}`,
  );
  handleUnauthorized(response);
  if (!response.ok) throw await responseError(response);
  const payload = (await response.json()) as SupportIssueDashboardResponse;
  return {
    issues: Array.isArray(payload.issues) ? payload.issues : [],
    summary: payload.summary,
  };
}

export async function fetchSupportAssignees(
  developerView = false
): Promise<SupportAssignee[]> {
  if (process.env.REACT_APP_USE_MOCK === 'true') {
    return mockSupportAssignees.map((source) => {
      const assignee = { ...source };
      if (developerView) return assignee;
      const { github_login: _githubLogin, ...partnerAssignee } = assignee;
      return partnerAssignee;
    });
  }
  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/admin/support/assignees`, {
    credentials: 'include',
  });
  handleUnauthorized(response);
  if (!response.ok) throw await responseError(response);
  const payload = (await response.json()) as AssigneeListResponse;
  return Array.isArray(payload.assignees) ? payload.assignees : [];
}

export async function updateSupportIssue(
  issueId: string,
  changes: SupportIssueAdminPatch
): Promise<SupportIssue> {
  if (process.env.REACT_APP_USE_MOCK === 'true') {
    const issue = mockIssueState.find((row) => row.id === issueId);
    if (!issue) throw new Error('Issue not found.');
    return replaceMockIssue({
      ...issue,
      ...changes,
      updated_at: new Date().toISOString(),
    });
  }
  const response = await fetchWithChatFeedback(
    `${API_BASE_URL}/api/admin/support/issues/${encodeURIComponent(issueId)}`,
    {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(changes),
    }
  );
  handleUnauthorized(response);
  if (!response.ok) throw await responseError(response);
  const payload = (await response.json()) as IssueResponse;
  return payload.issue;
}

export async function publishSupportIssueCustomerUpdate(
  issueId: string,
  message: string,
  updateId: string
): Promise<SupportIssue> {
  if (process.env.REACT_APP_USE_MOCK === 'true') {
    const issue = mockIssueState.find((row) => row.id === issueId);
    if (!issue) throw new Error('Issue not found.');
    const mockUpdateId = `mock-update-${updateId}`;
    const existing = issue.customer_updates.find(
      (update) => update.id === mockUpdateId
    );
    if (existing) {
      if (existing.message !== message) {
        throw new Error(
          'Customer update identifier was reused with different text.'
        );
      }
      return issue;
    }
    const timestamp = new Date().toISOString();
    return replaceMockIssue({
      ...issue,
      updated_at: timestamp,
      customer_update_version: issue.customer_update_version + 1,
      has_unread_update: true,
      customer_updates: [
        {
          id: mockUpdateId,
          message,
          published_at: timestamp,
          updated_at: timestamp,
        },
        ...issue.customer_updates,
      ],
    });
  }
  const response = await fetchWithChatFeedback(
    `${API_BASE_URL}/api/admin/support/issues/${encodeURIComponent(
      issueId
    )}/customer-updates`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message, update_id: updateId }),
    }
  );
  handleUnauthorized(response);
  if (!response.ok) throw await responseError(response);
  const payload = (await response.json()) as IssueResponse;
  return payload.issue;
}

export async function publishSupportIssueInternalNote(
  issueId: string,
  message: string,
  noteId: string
): Promise<SupportIssue> {
  if (process.env.REACT_APP_USE_MOCK === 'true') {
    const issue = mockIssueState.find((row) => row.id === issueId);
    if (!issue) throw new Error('Issue not found.');
    const mockNoteId = `mock-internal-note-${noteId}`;
    const existing = issue.internal_notes?.find(
      (note) => note.id === mockNoteId
    );
    if (existing) {
      if (existing.message !== message) {
        throw new Error('Internal note identifier was reused with different text.');
      }
      return cloneSupportIssue(issue);
    }
    const timestamp = new Date().toISOString();
    return replaceMockIssue({
      ...issue,
      updated_at: timestamp,
      internal_notes: [
        {
          id: mockNoteId,
          message,
          published_at: timestamp,
          updated_at: timestamp,
        },
        ...(issue.internal_notes || []),
      ],
    });
  }
  const response = await fetchWithChatFeedback(
    `${API_BASE_URL}/api/admin/support/issues/${encodeURIComponent(
      issueId
    )}/internal-notes`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message, note_id: noteId }),
    }
  );
  handleUnauthorized(response);
  if (!response.ok) throw await responseError(response);
  const payload = (await response.json()) as IssueResponse;
  return payload.issue;
}

export async function resolveSupportIssue(
  issueId: string,
  resolutionSummary: string,
  updateId: string
): Promise<SupportIssue> {
  if (process.env.REACT_APP_USE_MOCK === 'true') {
    const issue = mockIssueState.find((row) => row.id === issueId);
    if (!issue) throw new Error('Issue not found.');
    const timestamp = new Date().toISOString();
    const mockUpdateId = `mock-resolution-${updateId}`;
    const anonymousError = issue.kind === 'error' && issue.user_determined === false;
    if (anonymousError) {
      const existing = issue.internal_resolutions?.find(
        (resolution) => resolution.id === mockUpdateId,
      );
      return replaceMockIssue({
        ...issue,
        status: 'completed',
        completed_at: timestamp,
        updated_at: timestamp,
        internal_resolutions: existing
          ? issue.internal_resolutions
          : [
              {
                id: mockUpdateId,
                message: resolutionSummary,
                published_at: timestamp,
                updated_at: timestamp,
              },
              ...(issue.internal_resolutions || []),
            ],
      });
    }
    const existing = issue.customer_updates.find(
      (update) => update.id === mockUpdateId
    );
    return replaceMockIssue({
      ...issue,
      status: 'completed',
      completed_at: timestamp,
      updated_at: timestamp,
      customer_update_version:
        issue.customer_update_version + (existing ? 0 : 1),
      has_unread_update: true,
      customer_updates: existing
        ? issue.customer_updates
        : [
            {
              id: mockUpdateId,
              message: resolutionSummary,
              published_at: timestamp,
              updated_at: timestamp,
            },
            ...issue.customer_updates,
          ],
    });
  }
  const response = await fetchWithChatFeedback(
    `${API_BASE_URL}/api/admin/support/issues/${encodeURIComponent(
      issueId
    )}/resolve`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: resolutionSummary,
        update_id: updateId,
      }),
    }
  );
  handleUnauthorized(response);
  if (!response.ok) throw await responseError(response);
  const payload = (await response.json()) as IssueResponse;
  return payload.issue;
}

export async function markSupportIssueRead(
  issueId: string,
  customerUpdateVersion: number
): Promise<SupportIssue> {
  if (process.env.REACT_APP_USE_MOCK === 'true') {
    const issue = mockIssueState.find((row) => row.id === issueId);
    if (!issue) throw new Error('Issue not found.');
    return replaceMockIssue({ ...issue, has_unread_update: false });
  }
  const response = await fetchWithChatFeedback(
    `${API_BASE_URL}/api/support/issues/${encodeURIComponent(issueId)}/mark-read`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        customer_update_version: customerUpdateVersion,
      }),
    }
  );
  handleUnauthorized(response);
  if (!response.ok) throw await responseError(response);
  const payload = (await response.json()) as IssueResponse;
  return payload.issue;
}

/** Add an append-only follow-up to the authenticated owner's own feedback. */
export async function addSupportIssueCustomerComment(
  issueId: string,
  message: string,
  commentId: string
): Promise<SupportIssue> {
  if (process.env.REACT_APP_USE_MOCK === 'true') {
    const issue = mockIssueState.find((row) => row.id === issueId);
    if (!issue) throw new Error('Issue not found.');
    const normalizedMessage = message.trim();
    const mockCommentId = `mock-followup-${commentId}`;
    const existing = issue.customer_comments.find(
      (comment) => comment.id === mockCommentId
    );
    if (existing) {
      if (existing.message !== normalizedMessage) {
        throw new Error('Customer comment identifier was reused with different text.');
      }
      return cloneSupportIssue(issue);
    }
    const timestamp = new Date().toISOString();
    return replaceMockIssue({
      ...issue,
      updated_at: timestamp,
      customer_comments: [
        {
          id: mockCommentId,
          message: normalizedMessage,
          published_at: timestamp,
          updated_at: timestamp,
        },
        ...issue.customer_comments,
      ],
    });
  }
  const response = await fetchWithChatFeedback(
    `${API_BASE_URL}/api/support/issues/${encodeURIComponent(
      issueId
    )}/customer-comments`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message, comment_id: commentId }),
    }
  );
  handleUnauthorized(response);
  if (!response.ok) throw await responseError(response);
  const payload = (await response.json()) as IssueResponse;
  return payload.issue;
}

/** Acknowledge only the customer-comment version actually rendered to an admin. */
export async function markSupportIssueCustomerCommentsRead(
  issueId: string,
  customerCommentVersion: number
): Promise<SupportIssue> {
  if (process.env.REACT_APP_USE_MOCK === 'true') {
    const issue = mockIssueState.find((row) => row.id === issueId);
    if (!issue) throw new Error('Issue not found.');
    return replaceMockIssue({
      ...issue,
      has_unread_customer_comment: false,
    });
  }
  const response = await fetchWithChatFeedback(
    `${API_BASE_URL}/api/support/issues/${encodeURIComponent(
      issueId
    )}/customer-comments/mark-read`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        customer_comment_version: customerCommentVersion,
      }),
    }
  );
  handleUnauthorized(response);
  if (!response.ok) throw await responseError(response);
  const payload = (await response.json()) as IssueResponse;
  return payload.issue;
}

export async function fetchSupportIssueUnreadCount(): Promise<number> {
  if (process.env.REACT_APP_USE_MOCK === 'true') {
    return mockIssueState.filter((issue) => issue.has_unread_update).length;
  }
  try {
    const response = await fetchWithChatFeedback(
      `${API_BASE_URL}/api/support/issues/unread-count`,
      { credentials: 'include' }
    );
    handleUnauthorized(response);
    if (!response.ok) return 0;
    const payload = await response.json();
    return typeof payload?.count === 'number' ? payload.count : 0;
  } catch {
    return 0;
  }
}
